import React from "react";
import { useState } from "react";
import { SmilePlus, PieChart, BarChart2, History, ChartBar } from "lucide-react";
import BarGraph2 from "../components/BarGraph2";
import PieGraph from "../components/PieGraph";
import StudentPieGraph from "./StudentPieGraph";
import TimeCompareDropdown from "../../StudentData/Dropdowns/TimeCompareDropdown";
import DateframeDropdownSelect from "../../../components/DateframeDropdownSelect";
import CompareDropdown from "./CompareDropdown";
import { getEmojiImage, emotionColors, barEmotionOrder } from "../utils";
import BigFiveWellnessDataWrapper from "../../../components/BigFiveWellnessData";
import moment from "moment";
import { timeHelper } from "../../StudentData/utils/timeHelper";
import { processBigFive } from "../../StudentData/handlers/bigFiveHandler";

const StudentEmotionsDistribution = ({
  state,
  emotionPercents,
  tally,
  userRole,
  gatherWellnessPractices,
  filterName,
  feeling,
  bigFiveFilteredData,
  selectedTimes,
  setSelectedTimes,
  helpfulFilter,
  setHelpfulFilter,
  selectedSubOptions,
  setSelectedSubOptions,
  wellnessPopupOpen,
  setWellnessPopupOpen,
  adultActionImages,
}) => {
  const [activeChart, setActiveChart] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 640 ? "bar" : "pie"
  );

  const numberWithCommas = (x) => {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const CustomTooltip = ({ active, payload, label, tally }) => {
    if (active && payload && payload.length) {
      const { name, count } = payload[0].payload;
      const percentage = tally ? Math.round((count / tally) * 100) : 0;
      const countText =
        count === 1 ? "1 time" : `${numberWithCommas(count)} times`;
      return (
        <div className="custom-tooltip">
          <p className="label">{`${name} : ${countText} (${percentage}%)`}</p>
        </div>
      );
    }
    return null;
  };

  const BarGraphComponent = (props) => {
    return (
      <div>
        <div>
          <div className="flex items-center gap-3 mb-3 bg-white p-2 rounded justify-end w-full mt-6"></div>
          <div className="w-auto h-[350px] m-auto pt-2 box-border">
            <BarGraph2
              state={state}
              barGraphWithPercent={emotionPercents}
              CustomTooltip={(tooltipProps) => (
                <CustomTooltip {...tooltipProps} tally={props.tally} />
              )}
            />
          </div>
        </div>
      </div>
    );
  };

  const PieGraphComponent = (props) => {
    return (
      <div>
        <div>
          <StudentPieGraph
            pieGraphData={emotionPercents}
            tally={props.tally}
            userRole={props.userRole}
          />
        </div>
      </div>
    );
  };

  const currentDateForHistory = moment().subtract(timeHelper(filterName));
  const bigFiveDataFiltered = [];
  (bigFiveFilteredData || []).forEach((dp) => {
    processBigFive(
      selectedTimes,
      selectedSubOptions,
      helpfulFilter,
      bigFiveDataFiltered,
      { ...dp },
      currentDateForHistory,
      dp?.email
    );
  });

  const renderChart = () => {
    switch (activeChart) {
      case "pie":
        return <PieGraphComponent tally={tally} userRole={userRole} />;
      case "bar":
        return <BarGraphComponent tally={tally} />;
      case "history":
        return (
          <div className="mt-4">
            <BigFiveWellnessDataWrapper
              bigFiveFilteredData={bigFiveDataFiltered}
              selectedTimes={selectedTimes}
              setSelectedTimes={setSelectedTimes}
              helpfulFilter={helpfulFilter}
              setHelpfulFilter={setHelpfulFilter}
              selectedSubOptions={selectedSubOptions}
              setSelectedSubOptions={setSelectedSubOptions}
              wellnessPopupOpen={wellnessPopupOpen}
              setWellnessPopupOpen={setWellnessPopupOpen}
              adultActionImages={adultActionImages}
            />
          </div>
        );
      default:
        return (
          <div className="h-64 bg-gray-50 rounded flex items-center justify-center">
            Select a chart type
          </div>
        );
    }
  };

  // Prepare ordered emotions for mobile list view
  const orderedEmotions = (barEmotionOrder || [])
    .map((emotion) => emotionPercents.find((e) => e.name === emotion))
    .filter(Boolean);

  return (
    <div
      className="flex-1 bg-white shadow-md p-3 sm:p-4 2xl:p-8 rounded"
      data-onboarding-id="emotions-breakdown"
    >
      <div className="flex justify-between items-center mb-2 sm:mb-3 gap-2">
        <div className="flex items-center gap-2">
          <SmilePlus className="w-5 h-5" />
          <h3 className="text-xl sm:text-2xl font-bold">My Emotions</h3>
        </div>
        <div className="sm:flex space-x-1 hidden">
          <button
            className={`p-1 hover:bg-gray-100 border-b-2 ${
              activeChart === "pie"
                ? "border-black text-black"
                : "border-transparent text-gray-600"
            }`}
            onClick={() => setActiveChart("pie")}
            aria-label="Switch to Pie Chart"
          >
            <PieChart className="w-4 h-4" />
          </button>
          <button
            className={`p-1 hover:bg-gray-100 border-b-2 ${
              activeChart === "bar"
                ? "border-black text-black"
                : "border-transparent text-gray-600"
            }`}
            onClick={() => setActiveChart("bar")}
            aria-label="Switch to Bar Chart"
          >
            <BarChart2 className="w-4 h-4" />
          </button>
          <button
            className={`p-1 hover:bg-gray-100 border-b-2 ${
              activeChart === "history"
                ? "border-black text-black"
                : "border-transparent text-gray-600"
            }`}
            onClick={() => setActiveChart("history")}
            aria-label="Switch to History"
          >
            <History className="w-4 h-4" />
          </button>
        </div>
        {/* Mobile-only toggle*/}
        <div className="flex space-x-1 sm:hidden">
          <button
            className={`p-1 hover:bg-gray-100 border-b-2 ${
              activeChart === "bar"
                ? "border-black text-black"
                : "border-transparent text-gray-600"
            }`}
            onClick={() => setActiveChart("bar")}
            aria-label="Switch to Horizontal Bar"
            title="Horizontal Bar"
          >
            <ChartBar className="w-4 h-4" />
          </button>
          <button
            className={`p-1 hover:bg-gray-100 border-b-2 ${
              activeChart === "history"
                ? "border-black text-black"
                : "border-transparent text-gray-600"
            }`}
            onClick={() => setActiveChart("history")}
            aria-label="Switch to History"
          >
            <History className="w-4 h-4" />
          </button>
        </div>
      </div>
      <h4 className="text-400 text-lg ml-7">
        Discover which emotions are impacting you
      </h4>
      {/* Mobile-only: stacked bars */}
      {activeChart === "bar" && (
        <div className="sm:hidden mt-3 flex flex-col gap-3">
          {orderedEmotions.map((d) => {
            const percent =
              typeof d.currentPercent === "number"
                ? d.currentPercent
                : tally
                ? Math.round(((d.count || 0) / tally) * 100)
                : 0;
            const color = emotionColors[d.name] || "#999999";
            return (
              <div key={d.name} className="flex items-center gap-3 w-full">
                <div className="flex items-center justify-start shrink-0 gap-1 w-24">
                  <img
                    src={getEmojiImage(d.name)}
                    alt={d.name}
                    className="w-6 h-6"
                    style={{ color: color }}
                  />
                  <span className="capitalize text-base shrink-0">
                    {d.name}
                  </span>
                </div>
                <div className="flex-1 h-6 w-28 bg-[#EEEEEE] rounded overflow-hidden">
                  <div
                    className="h-full rounded"
                    style={{ width: `${percent}%`, backgroundColor: color }}
                  />
                </div>
                <span className="text-right text-sm w-10 font-semibold">
                  {percent}%
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Mobile-only: history table */}
      {activeChart === "history" && (
        <div className="sm:hidden items-center justify-center mt-3">
          {renderChart()}
        </div>
      )}
      <div className="hidden sm:block items-center justify-center">
        {renderChart()}
      </div>
    </div>
  );
};

export default StudentEmotionsDistribution;
